<script setup>
import {ref, computed, onMounted, defineProps, defineExpose, defineEmits, nextTick} from 'vue'

const props = defineProps({
  modelValue: Object,
  names: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Введи своё имя'
  },
  alt: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const displayValue = ref('')
const showSuggestions = ref(false)
const selectedIndex = ref(-1)
const selectedName = ref(null)
const isValidName = ref(false)

onMounted(() => {
})

// Фильтрация имен по запросу - только начинающиеся с введенных букв
const filteredNames = computed(() => {
  if (!displayValue.value) return []

  const query = displayValue.value.toLowerCase()

  // Фильтруем только имена, которые начинаются с запроса (в имени или файле)
  const startsWithName = props.names.filter(name =>
      name.name.toLowerCase().startsWith(query) ||
      name.file.toLowerCase().startsWith(query)
  )

  return startsWithName;
})

// Проверка, является ли текущее значение валидным
const checkValidName = (value) => {
  if (!value) return false

  return props.names.some(name =>
      name.name.toLowerCase() === value.toLowerCase() ||
      name.file.toLowerCase() === value.toLowerCase()
  )
}

// Подсветка совпадающей части имени
const getHighlightedMatch = (name, file) => {
  if (!displayValue.value) return name

  const query = displayValue.value.toLowerCase()
  const nameLower = name.toLowerCase()
  const fileLower = file.toLowerCase()

  // Проверяем совпадение в начале имени
  if (nameLower.startsWith(query)) {
    const match = name.substring(0, query.length)
    const afterMatch = name.substring(query.length)
    return `<strong>${match}</strong>${afterMatch}`
  }

  // Проверяем совпадение в начале транслитерации
  if (fileLower.startsWith(query)) {
    return name
  }

  return name
}

const handleInput = () => {
  showSuggestions.value = true
  selectedIndex.value = -1

  // Проверяем валидность введенного имени
  isValidName.value = checkValidName(displayValue.value)

  if (displayValue.value) {
    const matchedName = props.names.find(name =>
        name.name.toLowerCase() === displayValue.value.toLowerCase() ||
        name.file.toLowerCase() === displayValue.value.toLowerCase()
    )
    if (matchedName) {
      // Если найдено точное совпадение
      emit('update:modelValue', { name: matchedName.name, file: matchedName.file })
      selectedName.value = matchedName
      isValidName.value = true
    } else {
      // Если значение не из списка, временно ничего не делаем
      // Ждем пока пользователь закончит ввод (blur или enter)
      emit('update:modelValue', { name: undefined, file: undefined })
      selectedName.value = { name: displayValue.value, file: undefined }
      isValidName.value = false
    }
  } else {
    // Если поле пустое
    emit('update:modelValue', null)
    selectedName.value = null
    isValidName.value = false
  }
}

const hideSuggestions = () => {
  nextTick(() => {
    showSuggestions.value = false

    // При потере фокуса если введено невалидное имя, подставляем "Другое имя"
    if (displayValue.value && !isValidName.value) {
      selectOtherName()
    }
  })
}

const moveSelection = (direction, event) => {
  // Предотвращаем стандартное поведение браузера (перемещение курсора)
  event.preventDefault()

  if (!showSuggestions.value) {
    showSuggestions.value = true
    return
  }

  const totalItems = filteredNames.value.length + 1 // +1 для опции "Другое имя"
  if (totalItems === 0) return

  // Инициализируем selectedIndex если он -1
  if (selectedIndex.value === -1) {
    selectedIndex.value = direction === 1 ? 0 : totalItems - 1
  } else {
    selectedIndex.value += direction

    if (selectedIndex.value < 0) {
      selectedIndex.value = totalItems - 1
    } else if (selectedIndex.value >= totalItems) {
      selectedIndex.value = 0
    }
  }
}

const handleEnter = (event) => {
  event.preventDefault()

  if (showSuggestions.value && selectedIndex.value >= 0) {
    // Если есть выделенный элемент в списке, выбираем его
    if (selectedIndex.value === 0) {
      selectOtherName()
    } else {
      const nameToSelect = filteredNames.value[selectedIndex.value - 1]
      if (nameToSelect) {
        selectName(nameToSelect)
      }
    }
  } else {
    // Если нажат Enter без выделенного элемента
    if (displayValue.value && !isValidName.value) {
      // Если введено значение не из списка - подставляем "Другое имя" в инпут
      selectOtherName()
    } else if (displayValue.value && isValidName.value) {
      // Если значение валидное, просто скрываем список
      showSuggestions.value = false
    }
  }
}

const selectName = (name = null) => {
  if (name) {
    displayValue.value = name.name
    emit('update:modelValue', { name: name.name, file: name.file })
    selectedName.value = name
    isValidName.value = true
    showSuggestions.value = false
    selectedIndex.value = -1
  }
}

const selectOtherName = () => {
  // Подставляем текст "Другое имя" в инпут
  displayValue.value = 'Другое имя'
  emit('update:modelValue', { name: 'Другое имя', file: 'other' })
  selectedName.value = { name: 'Другое имя', file: 'other' }
  isValidName.value = true
  showSuggestions.value = false
  selectedIndex.value = -1
}

defineExpose({
  selectedName
})
</script>

<template>
  <div :class="['autocomplete', {'autocomplete_alt': alt}]">
    <input
        v-model="displayValue"
        type="text"
        :placeholder="placeholder"
        @input="handleInput"
        @focus="showSuggestions = true"
        @blur="hideSuggestions"
        @keydown.down="moveSelection(1, $event)"
        @keydown.up="moveSelection(-1, $event)"
        @keydown.enter="handleEnter"
        class="autocomplete__input"
    />

    <ul v-show="showSuggestions && (filteredNames.length > 0 || displayValue)" class="autocomplete__suggestions">
      <!-- Опция "Другое имя" всегда первая -->
      <li
          :class="{ '_selected': selectedIndex === 0 }"
          @mousedown="selectOtherName"
          class="autocomplete__suggestions-item other-name"
      >
        <span>Другое имя</span>
      </li>

      <li
          v-for="(name, index) in filteredNames"
          :key="name.name"
          :class="{ '_selected': index + 1 === selectedIndex }"
          @mousedown="selectName(name)"
          class="autocomplete__suggestions-item"
      >
        <span class="_name-match" v-html="getHighlightedMatch(name.name, name.file)"></span>
      </li>
    </ul>
  </div>
</template>